/**
 * Static event content for the Visionaries Summit page. Hardcoded English
 * (UK event); shared across all locale prefixes.
 */

/**
 * Luma event page — the default CTA target for the shared `VsVenue` /
 * `VsNav` components, and also used by the sub-pages (Big Pitch, Pitch
 * Night).
 */
export const APPLY_HREF = "https://luma.com/visionaries-summit";

/**
 * Destination for every forward-looking CTA on the New York announcement
 * page ("Reserve your spot" in the nav, hero, and venue banner). Points
 * straight at the external Luma event page.
 */
export const REGISTER_INTEREST_HREF = APPLY_HREF;

export const EVENT_DETAILS = {
  brand: "Wayflyer Presents",
  title: "The Visionaries Summit.",
  description:
    "Three days in London with the founders building the UK's most exciting consumer brands. Held at Batch LDN, June 2026.",
  dates: "Wed 3 – Fri 5 June 2026 · Batch LDN",
  applyHref: APPLY_HREF,
  registerHref: REGISTER_INTEREST_HREF,
  speakersHref: "#speakers",
  pitchHref: "#big-pitch",
} as const;

export const ACTION_BAR_TEXT =
  "Next Visionaries Summit · KidSuper Studios, Brooklyn · Aug 26–27, 2026";

// No page-level nav links — the Summit is a single page now, so the nav is
// just the logo + CTA. (The Hackathon link and the self-referential
// "Visionaries Summit" link were both removed.)
export const NAV_LINKS: readonly { href: string; label: string }[] = [];

export const STATS = ["3 days", "9 panels", "30 founders"] as const;

/**
 * Founder brand logos in the order they appear on the strip. Each asset is
 * hosted on the Sanity `redesign` dataset CDN (project `1aendizt`) and pre-
 * tinted to the muted neutral (~#929292) used in the Figma reference; SVGs
 * are vector, PNGs have transparent backgrounds.
 */
export const LOGOS = [
  {
    src: "https://cdn.sanity.io/files/1aendizt/redesign/d5b62d1f3cc3690d73550026d47472e4ac3318bc.svg",
    alt: "Cole Buxton",
    width: 176,
    height: 20,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/869342a78c9920668aadb693ecdcbf5a72081f82-1024x539.png",
    alt: "PerfectTed",
    width: 61,
    height: 32,
  },
  {
    src: "https://cdn.sanity.io/files/1aendizt/redesign/38d162d30de62f35dfa010cb11796b792fac0544.svg",
    alt: "Tonic Health",
    width: 73,
    height: 21,
  },
  {
    src: "https://cdn.sanity.io/files/1aendizt/redesign/f895552c1edec23e31c4ae4696b3cf7eb89bd7e8.svg",
    alt: "Manors Golf",
    width: 165,
    height: 24,
  },
  {
    src: "https://cdn.sanity.io/files/1aendizt/redesign/bbddc973093b84fd7dbd5ebfd42b3365b5003228.svg",
    alt: "Peachies",
    width: 121,
    height: 20,
  },
  {
    src: "https://cdn.sanity.io/files/1aendizt/redesign/6dfa5e43f7ff300664c81a0d41d1101011c880b5.svg",
    alt: "SURI",
    width: 97,
    height: 17,
  },
] as const;

/**
 * Founder brand logos for the New York Summit strip. Source marks came from
 * the brands' own Sanity assets (varied colors/formats); each was recolored
 * to the same muted neutral (~#929292) as `LOGOS` above and re-hosted locally
 * (`public/images/visionaries-summit/ny-logos/`) since no Sanity write access
 * was available at the time these were added.
 */
export const NY_LOGOS = [
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/7048d8e967d0f38d1eae569786f6c7f0f76dc9aa-700x244.png",
    alt: "Bobbie",
    width: 57,
    height: 20,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/a9815e6d1e9d9f348d98b169cfcc4daa83101446-497x434.png",
    alt: "Bangkok Supper Club",
    width: 37,
    height: 32,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/8284fa0cacb9a0996d1daf683c1316862ede82fd-655x372.png",
    alt: "Bub's Bakery",
    width: 49,
    height: 28,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/6e408989df9043f8df73d104fb5042a293960afe-700x120.png",
    alt: "Fish Cheeks",
    width: 93,
    height: 16,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/e4c162822ebc3a4857944a6b2d0eaf4d19dd7c0f-700x239.png",
    alt: "Butter Girl",
    width: 82,
    height: 28,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/cfb35436d29b7517d2d3fb2a665177cdab0e65f0-700x219.png",
    alt: "Jones",
    width: 64,
    height: 20,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/33d38ae5665aa77b625e9db23d83261b1a09b3bd-488x249.png",
    alt: "Noonworld",
    width: 55,
    height: 28,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/200c1d5ce33f0f0e34fbc5f3d1dda71e94057bbc-700x196.png",
    alt: "Sourmilk",
    width: 64,
    height: 18,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/b86a1e42c663c7f26adf8e15624a41f723d5b125-700x241.png",
    alt: "Stakt",
    width: 58,
    height: 20,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/3da919b03d4ca993bf52f0fb4fe98a738c90f8c7-700x492.png",
    alt: "Surfing Cow",
    width: 46,
    height: 32,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/18a3b7c347292216d3e6d3189265e624a408f29d-700x296.png",
    alt: "Ghia",
    width: 47,
    height: 20,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/4852a29fbd112f48cf6f1bc8fa51926a940df917-292x234.png",
    alt: "Lore Bathing Club",
    width: 40,
    height: 32,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/6425568a22455145835b14875f2e5b31bd2429ce-1510x342.png",
    alt: "TALEA Beer Co.",
    width: 88,
    height: 20,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/3a5c9d35a6d068c8f056d19d62a0a7d2307ad7e3-1410x1231.svg",
    alt: "Act + Acre",
    width: 37,
    height: 32,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/b577c3c03c18e6f02eaef0d8d87bcb77fa802b8f-5799x1692.png",
    alt: "Loftie",
    width: 69,
    height: 20,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/2da4d2569b191071451f97100da914b00835f6a0-547x257.svg",
    alt: "Sauz",
    width: 43,
    height: 20,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/08ea4928b806c8ab0047eb409d4fe5d52679865a-1814x1758.png",
    alt: "Sky High Farm Goods",
    width: 33,
    height: 32,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/78088e8f20e351600498e5469bc06165f451ad5b-445x359.png",
    alt: "Dad Gang",
    width: 35,
    height: 28,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/2e5a9e78f3297118bc8d8fb7ae018fde81c4d4fc-195x49.png",
    alt: "Staple",
    width: 64,
    height: 16,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/0f829f893f51fce1a9fce67f7753b9395ee0106f-675x185.png",
    alt: "Little Spoon",
    width: 73,
    height: 20,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/d0bbbb45c9873d60643bd57d216320a582c995e2-2371x849.png",
    alt: "Le Alfré",
    width: 56,
    height: 20,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/d2d57244cc699f5b743812fd692f986cddf6a2af-1202x370.png",
    alt: "August",
    width: 65,
    height: 20,
  },
  {
    src: "/images/visionaries-summit/ny-logos/kidsuper.png",
    alt: "KidSuper",
    width: 79,
    height: 20,
  },
] as const;

/**
 * Combined London + New York brand logos for the retrospective page — every
 * founder brand that joined either summit, interleaved (rather than grouped
 * by city) so the marquee doesn't read as two separate strips.
 */
export const ALL_LOGOS = [
  NY_LOGOS[0],
  NY_LOGOS[1],
  LOGOS[0],
  NY_LOGOS[2],
  NY_LOGOS[3],
  LOGOS[1],
  NY_LOGOS[4],
  NY_LOGOS[5],
  NY_LOGOS[6],
  LOGOS[2],
  NY_LOGOS[7],
  NY_LOGOS[8],
  NY_LOGOS[9],
  LOGOS[3],
  NY_LOGOS[10],
  NY_LOGOS[11],
  LOGOS[4],
  NY_LOGOS[12],
  NY_LOGOS[13],
  NY_LOGOS[14],
  LOGOS[5],
  NY_LOGOS[15],
  NY_LOGOS[16],
  NY_LOGOS[17],
  NY_LOGOS[18],
  NY_LOGOS[19],
  NY_LOGOS[20],
  NY_LOGOS[21],
  NY_LOGOS[22],
] as const;

export type SpeakerLocation = "London" | "New York";

export interface Speaker {
  /** Headshot URL. Optional — placeholder cards render a neutral avatar. */
  src?: string;
  name: string;
  brand: string;
  /** Which summit this speaker appeared at — rendered as a small badge on
   *  their card in the combined `ALL_SPEAKERS` grid. */
  location: SpeakerLocation;
  /** Temporarily hidden from the grid (e.g. still being confirmed) without
   *  losing the entry — `VsSpeakers` filters these out before rendering. */
  hidden?: boolean;
}

/**
 * Speakers shown in the carousel — order matches Figma frame `555:433`. All
 * thumbnails are hosted on the Sanity `redesign` dataset CDN (project
 * `1aendizt`). The full twenty-five-speaker line-up is teased via the
 * "+14 more pinned up between now and June." line beneath the carousel.
 */
export const SPEAKERS: readonly Speaker[] = [
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/96b9061a1f0f5f4825f0333147844efd71b275a5-670x522.png",
    name: "Jonny & Cole",
    brand: "Cole Buxton",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/1b00347b98a417899b45cac8eb307b8d3a84ac3c-960x1440.png",
    name: "Jojo Regan",
    brand: "Manors Golf",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/48afe6269120ff67c5440c4fbe8eb0469c626c6a-370x462.jpg",
    name: "Marisa Poster",
    brand: "PerfectTed",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/9bfd2575b5ea4fcff13c555fd7dcc6e77915e157-1097x731.jpg",
    name: "Jamie Laing",
    brand: "Candy Kittens",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/ecae70036342947b188415d120be63f83189ee2a-2312x2560.png",
    name: "Rosie Connolly",
    brand: "4th Arq",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/f2138be01e265a7c543c5b0edd43aff12c2cc340-1562x1814.png",
    name: "Sunna Van Kampen",
    brand: "Tonic Health",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/705f5f8970d5220c1ae3540fff50d5a2188ccf56-2731x4096.png",
    name: "Rima Suppan",
    brand: "Peachies",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/2381dda1301dbee1521f939e76757dfc3682adfc-1310x1600.png",
    name: "Dom Day",
    brand: "FourFive",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/b5829dd7661c4ba196cd89a18fdbb294e07e7f54-956x1182.png",
    name: "Henry & Milly",
    brand: "Sult",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/4cd8b22ad78b267180e99f2c135f82e5045e5f89-1920x1130.png",
    name: "Julian and Sam",
    brand: "Batch LDN",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/65e895ff4daefca6201e0f5ac10a193ae5eb3a1c-2000x2000.png",
    name: "Mark Rushmore",
    brand: "Suri",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/d2bf6cd2994e945e6383562b275badbde48f5038-739x415.jpg",
    name: "Joyce De Haas",
    brand: "Double Dutch",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/e28eecd41cf3322eae8c50ac6360870f7fc090de-960x640.jpg",
    name: "Zain Peer",
    brand: "Nootropics",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/01c6c9074e2bfe000910cc1d87c36914b8d2faf4-1200x700.webp",
    name: "Spencer Matthews",
    brand: "CleanCo",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/4fee7d913b738aa952e08609326cf8e0a3d02189-800x800.jpg",
    name: "Lottie Whyte",
    brand: "MyoMaster",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/8c16346165bb24f31ae7d72b224db311135d5d0f-200x200.jpg",
    name: "Mia Smith",
    brand: "Healf",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/3e6e8de1360c74fae354c3155fb460609373eeaa-500x500.webp",
    name: "Laura Harnett",
    brand: "Seep",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/33e5e4df5adc3cb2dfe5b2581c9a2cd980a69368-570x400.webp",
    name: "Adrian Gilbane",
    brand: "Sons",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/9d6aeb46453973fb6a42b45065fcfb8abd776688-200x199.jpg",
    name: "Hannah Graham",
    brand: "Trip",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/77cab39aa3be303fe7500ded94dae0ae9c8368b5-547x365.jpg",
    name: "Aine Kilkenny",
    brand: "Riley",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/1e27ce93068478504b4a67d6965021db99b9054e-200x200.jpg",
    name: "Hywel Rose",
    brand: "Eleat",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/d84bb3ed68a919d71cf81c025b7aa0fef2a2ca7b-200x200.jpg",
    name: "Amie Caswell",
    brand: "Lick",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/b36247a54f426321b6ce9a9cda3a74a20df9538d-8256x6192.jpg",
    name: "Alana McFarlane",
    brand: "Gut Stuff",
    location: "London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/51af8b249232ebdeeb86a4198ec5b2ba7603c3b9-560x357.jpg",
    name: "Amy Moring",
    brand: "Hunter & Gather",
    location: "London",
  },
];

/**
 * New York speaker + moderator line-up (confirmed roster). Separate grid
 * from `SPEAKERS` (the London "Previous Visionaries" recap) — rendered
 * under its own "Speakers" heading. Entries without a headshot yet render
 * the placeholder avatar (see `VsSpeakers`) — photos still to come for
 * those; update `src` here once they land.
 */
export const NY_SPEAKERS: readonly Speaker[] = [
  // Brand founders first, moderators pushed to the bottom (see the second
  // group below) — the roster reads as "founders you're coming to see"
  // before "who's hosting the panels". Now that 8 men are confirmed
  // (Alec Merlino, James O'Reilly, Colm Mackin, Matt Hassett, Grant Eastey,
  // Bart Szaniewski, Harrison Glatt, Jeff Staple) they're alternated one
  // roughly every other card instead of left clustered wherever they were
  // added, so the strip doesn't read as skewed to either gender at a
  // glance. The extra women (11 vs 8 men) trail at the very end.
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/049c7f004dff4a1414c74c4a587b193d2e287024-5000x4000.jpg",
    name: "Kirsten Couchman",
    brand: "Sourmilk",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/2149dbe0ac418d8e2986994b490a0926c0c3192c-1080x1350.png",
    name: "Alec Merlino",
    brand: "Surfing Cow",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/bcd00560c853681e2b2a50fcb5b60110a5f6d7cc-2645x3967.jpg",
    name: "Millie & Taylor",
    brand: "STAKT",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/425d42cc2ce2031819efaaf99f8e7d259c310ac0-1674x2732.png",
    name: "James O'Reilly",
    brand: "Lore Bathing Club",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/be38e22f33cd4da92781926c9363db954600bd9a-5352x8024.jpg",
    name: "Melanie Masarin",
    brand: "Ghia",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/2a52f15413896352096841481bfcdffaed80b046-800x800.jpg",
    name: "Colm Mackin",
    brand: "Act + Acre",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/c73d6f727c0a2c6b0f5e225ce4cdeb7b1dbb5c75-4000x6000.jpg",
    name: "Jenna Saesue",
    brand: "55 Hospitality",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/e6c55370ade694be1bdeafef585257025b76d527-4000x6000.jpg",
    name: "Grant Eastey",
    brand: "Dad Gang",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/5c15a349edafccef850001086d9945edb2fcd82a-4000x6000.png",
    name: "Greer Hiltabidle",
    brand: "Butter Girl",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/c32468fe79ff809cacca7bac6d17c073d39bf36c-2832x2783.jpg",
    name: "Matt Hassett",
    brand: "Loftie",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/473ab2fa852f0f99efff9af7c002b370da31b27c-1206x1734.jpg",
    name: "Blair Pfander",
    brand: "Bobbie",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/1aadf55b6708b61a392edff6b917ee6cce6d2fa0-1271x1500.jpg",
    name: "Bart Szaniewski",
    brand: "Dad Gang",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/cc3cc2aa9adf850c8a222739327940bdfeb7d4b3-3072x4639.jpg",
    name: "Jane Kate Wong",
    brand: "NOON WORLD",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/7d18b9637e95cf2c90e5953b5462c5b575f8757e-1902x2848.jpg",
    name: "Harrison Glatt",
    brand: "Little Spoon",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/bcccfe997ef210aeb1779dcb7e875723ed94b006-3436x5154.jpg",
    name: "Hilary Dubin",
    brand: "Jones",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/9d8177d090944314fe196e550191534ac7ec0065-934x948.png",
    name: "Jeff Staple",
    brand: "Staple",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/ea5f1e60346206873539cb221c38b5d52fab43b2-1482x3308.jpg",
    name: "Allie Eatroff",
    brand: "Sauz",
    location: "New York",
  },
  {
    // Cropped to just LeAnn (the source photo has a second TALEA colleague
    // alongside her) and re-hosted locally — see NY_LOGOS above for why
    // (no Sanity write access in this environment).
    src: "https://cdn.sanity.io/images/1aendizt/redesign/e7e3048e034827d5e0ca7ce5eca9616e963df894-650x900.jpg",
    name: "LeAnn Darland",
    brand: "TALEA Beer Co.",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/2a76365ae8f3a2c07d90292cd71a2688ba7bc7a5-6192x8256.jpg",
    name: "Daphne Seybold",
    brand: "Sky High Farm Goods",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/a80f9dc56008bfc6dea087b97455e5350f3e8bec-1620x2025.png",
    name: "Alfré Snower",
    brand: "La Alfré",
    location: "New York",
  },
  {
    // Cropped to remove a second person's hair visible at the left edge of
    // the source photo, and re-hosted locally — see NY_LOGOS above for why
    // (no Sanity write access in this environment).
    src: "https://cdn.sanity.io/images/1aendizt/redesign/7803693c389d78947201d0dcc9b9636a08d63a07-748x1080.jpg",
    name: "Nick Jain",
    brand: "August",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/6b865a5aebabf2cac61c3512994a79bf5281a455-4270x6405.jpg",
    name: "Colm Dillane",
    brand: "KidSuper",
    location: "New York",
  },

  // Still-pending photos (hidden — see VsSpeakers), kept with their brand
  // for context.
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/62413a7263c32cf14e04ee94b9e4e517e86cb860-900x900.jpg",
    name: "Marcus Millione",
    brand: "Minted NY",
    location: "New York",
    hidden: true,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/35580d611ef6e927ca428b289da6883df1ee2c0c-1674x2732.png",
    name: "Adam Elzer",
    brand: "Lore Bathing Club",
    location: "New York",
    hidden: true,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/83c64ed358f05f1ea0b45b9e6fc6b422bd1b7e02-4299x6448.jpg",
    name: "Justin Felizzari",
    brand: "Cueva",
    location: "New York",
    hidden: true,
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/286494ee19f536b241cef837d972dee50fdd3a78-2740x4102.jpg",
    name: "Erik Anders Lang",
    brand: "Random Golf Club",
    location: "New York",
    hidden: true,
  },
  {
    name: "Amy or Maarten (TBC)",
    brand: "Loop",
    location: "New York",
    hidden: true,
  },

  // Moderators — pushed to the bottom of the visible grid (see comment
  // above); still first in line once "See more" is expanded past founders.
  {
    src: "https://cdn.sanity.io/files/1aendizt/redesign/408e5fbaee9d70e34de2143c9c10da9d74bcabae.png",
    name: "Rachel Wong",
    brand: "Monday Girl",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/3a8ee13f409559ca7ee9b04622f3533ca72fbea6-8503x11337.jpg",
    name: "Emma Forman",
    brand: "Myca Collective",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/a4c2f9402afa66d45b5b6c8aca43885b8161dbb9-1132x1132.png",
    name: "Emma Chozick",
    brand: "Gr8 Collab",
    location: "New York",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/b7a88353d8037c43313e2c8e5aa1364286ce11c3-4000x6000.jpg",
    name: "Emily Steele",
    brand: "Hummingbirds",
    location: "New York",
  },
] as const;

/**
 * Alternates two lists one-for-one (`a[0], b[0], a[1], b[1], ...`),
 * appending whatever's left once the shorter list runs out. Used to build
 * `ALL_SPEAKERS` and `HIGHLIGHT_CLIPS` without grouping by city.
 */
function alternate<T>(a: readonly T[], b: readonly T[]): readonly T[] {
  const queueA = [...a];
  const queueB = [...b];
  const merged: T[] = [];
  while (queueA.length > 0 || queueB.length > 0) {
    const nextA = queueA.shift();
    if (nextA) merged.push(nextA);
    const nextB = queueB.shift();
    if (nextB) merged.push(nextB);
  }
  return merged;
}

/**
 * Combined London + New York speaker roster for the single retrospective
 * "Speakers." section — every confirmed founder from both summits,
 * interleaved (rather than grouped by city) so the grid reads as one
 * roster. Each card carries its own `location` (see `Speaker`), rendered as
 * a small badge by `VsSpeakers`.
 *
 * All 24 London speakers alternate with the first 22 New York entries (the
 * confirmed, non-pending, non-moderator roster); the still-pending NY
 * entries (`hidden: true`) and the NY moderators are appended after —
 * position doesn't matter for the pending ones since `VsSpeakers` filters
 * them out, and the moderators trail intentionally, same as in
 * `NY_SPEAKERS`.
 */
export const ALL_SPEAKERS: readonly Speaker[] = [
  ...alternate(SPEAKERS, NY_SPEAKERS.slice(0, 22)),
  ...NY_SPEAKERS.slice(22, 27),
  ...NY_SPEAKERS.slice(27),
];

export interface ScheduleSession {
  time: string;
  title: string;
  speakers: string;
  /**
   * Per-session recording URL on the external video host (YouTube/Vimeo).
   * Optional: when omitted, the row renders a muted "Recording coming soon"
   * state instead of an active "Watch" link, so the agenda degrades
   * gracefully until the panel videos are published.
   * TODO(visionaries): paste each panel's YouTube link here as they go live.
   */
  videoHref?: string;
  /**
   * Per-session thumbnail. Sourced 1:1 from Figma frame `536:468` (Agenda)
   * and hosted on the Sanity `redesign` dataset CDN (project `1aendizt`).
   */
  thumbSrc: string;
}

export interface ScheduleDay {
  day: string;
  sessions: ScheduleSession[];
}

export const SCHEDULE: ScheduleDay[] = [
  {
    day: "Wednesday",
    sessions: [
      {
        time: "2 pm",
        title: "Understanding the Modern Female Wellness Consumer",
        speakers:
          "Lottie Whyte (MyoMaster), Alana McFarlane (Gut Stuff), Amy Moring (Hunter & Gather)",
        videoHref: "https://www.youtube.com/watch?v=UyLU0W9YG2g",
        thumbSrc: "https://img.youtube.com/vi/UyLU0W9YG2g/maxresdefault.jpg",
      },
      {
        time: "4 pm",
        title:
          "How to Take Market Share from Players Who've Had It for Decades",
        speakers:
          "Laura Harnett (Seep), Aine Kilkenny (Riley), Rima Suppan (Peachies)",
        videoHref: "https://www.youtube.com/watch?v=K0bkDYbu10Q",
        thumbSrc: "https://img.youtube.com/vi/K0bkDYbu10Q/maxresdefault.jpg",
      },
      {
        time: "6 pm",
        title: "The Unfiltered Playbook for Scaling a Drinks Brand",
        speakers:
          "Kelly Shaw (PerfectTed), Raissa de Haas (Double Dutch), Hannah Graham (Trip)",
        videoHref: "https://www.youtube.com/watch?v=pY78Yywcn88",
        thumbSrc: "https://img.youtube.com/vi/pY78Yywcn88/maxresdefault.jpg",
      },
    ],
  },
  {
    day: "Thursday",
    sessions: [
      {
        time: "2 pm",
        title: "Building Credibility in the Crowded Wellness Space",
        speakers: "Mia Smith (Healf), Dom Day (FourFive), Hywel Rose (Eleat)",
        videoHref: "https://www.youtube.com/watch?v=mgkiwBshxTE",
        thumbSrc: "https://img.youtube.com/vi/mgkiwBshxTE/maxresdefault.jpg",
      },
      {
        time: "4 pm",
        title: "Entertain First, Sell Second: The Organic Content Playbook",
        speakers:
          "Sunna Van Kampen (Tonic Health), Henry (Sult), Amie Caswell (Lick)",
        videoHref: "https://www.youtube.com/watch?v=MjYRwdvWliQ",
        thumbSrc: "https://img.youtube.com/vi/MjYRwdvWliQ/maxresdefault.jpg",
      },
      {
        time: "6 pm",
        title: "What Creator-Led Brands Know That Traditional Founders Don't",
        speakers:
          "Spencer Matthews (CleanCo), Jamie Laing (Candy Kittens), Rosie Connolly (4th Arq); moderated by Nadia Forde (Naere)",
        videoHref: "https://www.youtube.com/watch?v=yLoh1UBfyw0",
        thumbSrc: "https://img.youtube.com/vi/yLoh1UBfyw0/maxresdefault.jpg",
      },
    ],
  },
  {
    day: "Friday",
    sessions: [
      {
        time: "2 pm",
        title:
          "How to Build a Brand That Earns a Place in Your Customer's Daily Routine",
        speakers:
          "Mark Rushmore (Suri), Zain Peer (London Nootropics), Adrian Gilbane (Sons)",
        videoHref: "https://www.youtube.com/watch?v=RNSTjkF1wnc",
        thumbSrc: "https://img.youtube.com/vi/RNSTjkF1wnc/maxresdefault.jpg",
      },
      {
        time: "4 pm",
        title: "The Art of Creating Brand Universes",
        speakers: "Jonny Wilson (Cole Buxton), Barney Rhodes",
        videoHref: "https://www.youtube.com/watch?v=zmDTal_F_48",
        thumbSrc: "https://img.youtube.com/vi/zmDTal_F_48/maxresdefault.jpg",
      },
      {
        time: "6 pm",
        title: "How Investing in Brand Builds a Business That Lasts",
        speakers: "Jojo Regan (Manors Golf), Luke Davies (Manors Golf)",
        videoHref: "https://www.youtube.com/watch?v=IUkgMHhQc4M",
        thumbSrc: "https://img.youtube.com/vi/IUkgMHhQc4M/maxresdefault.jpg",
      },
    ],
  },
];

export interface PanelVideo {
  title: string;
  speakers: string;
  /**
   * Recording URL on the external video host (YouTube). Omitted while the
   * recording isn't published yet — the card renders "Recording coming
   * soon" instead.
   */
  videoHref?: string;
  thumbSrc: string;
}

/**
 * London panel recordings — flattened from `SCHEDULE` (same 9 sessions,
 * same thumbnails, `videoHref` set on each).
 */
export const LONDON_PANELS: readonly PanelVideo[] = SCHEDULE.flatMap((day) =>
  day.sessions.map(({ time: _time, ...session }) => session)
);

/**
 * New York panel recordings, same `PanelVideo` shape and YouTube thumbnail
 * pattern as `LONDON_PANELS`. `VsPanels` renders the New York grid whenever
 * this is non-empty.
 */
export const NY_PANELS: readonly PanelVideo[] = [
  {
    title: "How Some Brands Become Bigger Than What They Sell",
    speakers:
      "Jeff Staple (STAPLE), Daphne Seybold (Sky High Farm Goods), Alfré Snower (Le Alfré); in conversation with Shane Gillen",
    videoHref: "https://www.youtube.com/watch?v=5YTRVhI3iDk",
    thumbSrc: "https://img.youtube.com/vi/5YTRVhI3iDk/maxresdefault.jpg",
  },
  {
    title: "Choosing Partners That Build Long-Term Value, Not Just Hype",
    speakers:
      "Melanie Masarin (Ghia), James O'Reilly (Lore Bathing Club), Blair Pfander (Bobbie); moderated by Emma Apple Chozick (gr8 collab)",
    videoHref: "https://www.youtube.com/watch?v=MrcXpXE93GU",
    thumbSrc: "https://img.youtube.com/vi/MrcXpXE93GU/maxresdefault.jpg",
  },
  {
    title:
      "What High-Performing CPG Brands Do Differently at the Point of Purchase",
    speakers:
      "Dame, Jane Kate Wong (NOON WORLD), Nick Jain (August), Colm Mackin (Act + Acre); moderated by Emily Steele (Hummingbirds)",
    videoHref: "https://www.youtube.com/watch?v=oxpQzZNSJOI",
    thumbSrc: "https://img.youtube.com/vi/oxpQzZNSJOI/maxresdefault.jpg",
  },
  {
    title:
      "Dad Gang's Story: How 3 Dads Turned $750 Worth of Hats into a $40m Brand",
    speakers: "Bart Szaniewski, Grant Eastey, Ejay O'Donnell (Dad Gang)",
    videoHref: "https://www.youtube.com/watch?v=ZeQisbDTXac",
    thumbSrc: "https://img.youtube.com/vi/ZeQisbDTXac/maxresdefault.jpg",
  },
  {
    title: "How to Disrupt a Product Category That Already Seems Full",
    speakers:
      "Millie & Taylor (STAKT), Hilary Dubin (Jones), LeAnn Darland (TALEA Beer Co.); moderated by Rachel Wong (Monday Girl)",
    videoHref: "https://www.youtube.com/watch?v=J6o2zpHwpTo",
    thumbSrc: "https://img.youtube.com/vi/J6o2zpHwpTo/maxresdefault.jpg",
  },
  {
    title:
      "The Financial Realities of Scaling: Evolving Unit Economics as You Grow",
    speakers:
      "Harrison Glatt (Little Spoon), Alec Merlino (Surfing Cow), Matt Hassett (Loftie), Allie Eatroff (Sauz); moderated by Merry Nebiyu (Glimpse)",
    videoHref: "https://www.youtube.com/watch?v=zpqkyD2s9wQ",
    thumbSrc: "https://img.youtube.com/vi/zpqkyD2s9wQ/maxresdefault.jpg",
  },
  {
    title:
      "How to Build a Brand So Good That Customers Become Your Best Marketers",
    speakers:
      "Greer Hiltabidle (Butter Girl), Kirsten Couchman (Sourmilk), Jenna Saesue (55 Hospitality); moderated by Emma Forman (Myca Collective)",
    videoHref: "https://www.youtube.com/watch?v=okOxarwBt2k",
    thumbSrc: "https://img.youtube.com/vi/okOxarwBt2k/maxresdefault.jpg",
  },
  {
    title: "The Boy Who Jumped the Moon: A Conversation with KidSuper",
    speakers: "Colm Dillane (KidSuper)",
    videoHref: "https://www.youtube.com/watch?v=hJqI_jZi04U",
    thumbSrc: "https://img.youtube.com/vi/hJqI_jZi04U/maxresdefault.jpg",
  },
];

export interface ChapterDef {
  id: string;
  label: string;
}

export const CHAPTERS: ChapterDef[] = [
  { id: "speakers", label: "Previous Speakers" },
  { id: "panels", label: "Panels" },
  { id: "london", label: "Previously" },
  { id: "venue", label: "Venues" },
];

/**
 * Shared image/file URLs hosted on the Sanity `redesign` dataset CDN
 * (project `1aendizt`). See `scripts/.vs-asset-manifest.json` for the
 * `_id` ↔ URL mapping captured at upload time.
 */
export const ASSETS = {
  visionariesTitle:
    "https://cdn.sanity.io/images/1aendizt/redesign/bdbb42a347f923ed1d6ba4857f469441fd9bce9f-3840x2160.png",
  hackathonTitle:
    "https://cdn.sanity.io/images/1aendizt/redesign/d9219ccefb92d61e75b8f61345956d621edda391-1274x438.png",
  bigPitchBg:
    "https://cdn.sanity.io/images/1aendizt/redesign/3843f13a30a82959d302d01fb407681be741ca48-1376x768.png",
  venuePrimary:
    "https://cdn.sanity.io/images/1aendizt/redesign/cd10ed81f5b7241a9343d37c206dc11bf863eedd-2700x1803.png",
  venueInterior:
    "https://cdn.sanity.io/images/1aendizt/redesign/c65cc435def6edce7cc4b0ce503715d1383ab470-1661x969.png",
  venueMap:
    "https://cdn.sanity.io/images/1aendizt/redesign/4828f7127d9039e5291b2431e42816cbdf49fd23-981x657.png",
  scheduleClockIcon:
    "https://cdn.sanity.io/files/1aendizt/redesign/db04f94ddc9815c2005e7625aaa939c8bca27325.svg",
  heroVideo:
    "https://cdn.sanity.io/files/1aendizt/redesign/727aa3aa8cdc90e7abb49ca150bf0b67e2802c92.mp4",
  /** Static frame from `heroVideo`, extracted locally (no Sanity write access
   *  in this environment) — shown before the video paints and as a fallback
   *  whenever autoplay doesn't fire (e.g. mobile Safari over a poor
   *  connection or with Low Data Mode on). */
  heroPoster:
    "https://cdn.sanity.io/images/1aendizt/redesign/e2cab5c76ad121c268e2e0fd2ba884f681a5a501-1600x1205.jpg",
} as const;

/* ------------------------------------------------------------------ */
/* New York announcement — London as proof                            */
/* ------------------------------------------------------------------ */

/** Hero copy — retrospective (both the London and New York summits have
 *  now happened). */
export const HERO = {
  eyebrow: "Next Visionaries Summit: TBA",
  // Headline ("Visionaries Summit") is rendered directly in the hero.
  subhead:
    "Showcasing the teams behind the most exciting brands in the UK and US.",
  primaryCta: { label: "Register interest", href: "#register" },
  secondaryCta: { label: "Watch the panels", href: "#panels" },
} as const;

export interface RecapVideo {
  /** Mux playback ID. Preferred — streamed via the Mux player. */
  playbackId?: string;
  /** Highlight-edit recap video (mp4/webm). Fallback when no `playbackId`. */
  src?: string;
  /** Poster frame shown before playback and when nothing else is set. */
  poster?: string;
}

/**
 * London event recap video (fast-cut highlight edit), shown between the hero
 * CTAs and the brand logos. Plays muted on load with the player's
 * play/unmute + expand controls; falls back to the poster image, then a
 * neutral placeholder, when nothing is set.
 */
export const RECAP_VIDEO: RecapVideo = {
  playbackId: "zH01EpGx029uJQRW01FSo2Ta4Go2M1er2Bdzip00hPYPP48",
};

export interface GalleryImage {
  /** Image URL (host on the Sanity `redesign` CDN like the other assets). */
  src: string;
  /** Descriptive alt text for the photo. */
  alt: string;
}

/**
 * London event photo gallery — shown in its own dropdown by `VsGallery`,
 * alongside `NY_GALLERY_IMAGES` in a New York dropdown.
 */
export const LONDON_GALLERY_IMAGES: readonly GalleryImage[] = [
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/80a33138adad8e71d550abb09b02effccf3e5903-1366x2048.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 1)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/97f44bc85104146f99d79b04223d2b3ed2c8e5b8-1600x1066.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 2)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/943eaee718b6d85f80e9d95a913d29f19dab0907-1066x1600.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 3)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/25fcc7fd7aa24e3ae33f61b795676e37d002c152-1600x1066.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 4)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/195489635459095ddb4e646b1b35dda7edc6d3ef-1600x1066.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 5)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/608f5418eaacfd412d6257ec0ccc3eeca6c693c4-1600x1066.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 6)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/6b67e05b46fe67c880c74599e621390db2b8beab-2048x1366.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 7)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/8427b50b8f63c5b1cc32a691883bbc7e3b768058-1066x1600.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 8)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/92a1ab84e236ca614acc23608479e5c5053c8324-1600x1066.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 9)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/d39a059c45a02b246c0ac741964826a9794cc4e1-2048x1366.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 10)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/9c856af51aae34e5b7cfb72ca46d58309087eb02-2048x1300.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 11)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/e529756291f7c2a8ba4797622385a910013c21b9-1366x2048.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 12)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/3c5bf67bd2326e396960e24bc849e45e099071ff-2048x1288.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 13)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/54cd3646503c196b70902eabca306e3e79ca4cd9-2048x1366.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 14)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/d587bc271f6f954697df971cc1676f6e8ef91b73-1366x2048.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 15)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/a30f13fc9b957062e8afd829a1e0772644e212dd-2048x1366.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 16)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/e3cfc7d6641444324be45dc86dd607c836431eea-1366x2048.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 17)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/f6e895763c13d80e615da524ccf0f1b69bb6d379-2048x1366.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 18)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/e7fed1a51fdb19ac242bd778172092f57e903c3b-1366x2048.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 19)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/512e82c552a26c2f30b60a761e797a84a320c9e5-2048x1366.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 20)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/261bfba7979a1d64457e7f7d78f44767880cc297-1366x2048.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 21)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/752b1ef9aa97f9243aa617f06fd35ee0c7ff7c1e-2048x1366.jpg",
    alt: "Visionaries Summit at Batch LDN, London (photo 22)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/758d91cb666e1511b8d2bc6e796c2a7813a8d6ac-3024x4032.jpg",
    alt: "Visionaries Summit poster at Batch LDN, London",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/35cbf8117e5a1997272500df0a9675cdb0fda29a-4608x3456.jpg",
    alt: "Visionaries Summit menu at the Batch LDN dinner",
  },
];

/**
 * New York photo gallery — 34 photos from KidSuper Studios, Brooklyn
 * (sourced from the Sanity `redesign` dataset by original filename, e.g.
 * "WayflyerDay1_220.jpg", plus a few "_JD_" shots without a day in the
 * name). Add more in the same `GalleryImage` shape as `LONDON_GALLERY_IMAGES`.
 */
export const NY_GALLERY_IMAGES: readonly GalleryImage[] = [
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/4f6914f9bf2d8694b8954e050a6cd790666905d5-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 1)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/ea3961206814bc9c03d5ef5dd3d4392cf78b6f93-3600x2880.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 2)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/eadf6a624908440ae55734e91c83ca71321e7b5d-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 3)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/f014b3d04a015f647d35d7a5900a88b1e2a00836-3004x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 2, photo 4)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/57a6f93c8f929d70b28177fc97f2f790e4315883-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 5)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/03296d1c02419e01714031ce98a005676df4b06b-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 6)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/79c52b0d35e7f9400a5b522d68a4061e885cce5b-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 2, photo 7)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/7067e7a99fe276b6dfe549f0446ea73bb3776a9d-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 2, photo 8)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/a44229b04ca00a11cbf49affa199d9f5614ab2a1-3600x2400.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 9)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/9b3ecbaf0362a23c7ce9b11098e6b919b237bd31-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 2, photo 10)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/459efb9789ba4e956949504c676bbd193122b747-3600x2400.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 2, photo 11)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/2e29d20a3589f2cad1732423f8a75e1028c44e68-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 2, photo 12)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/6fff1d36c5f7f26c6f910f3840efef2b51738ffa-3600x2880.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (photo 13)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/5576a746671a200f44a42b5985aa2e3daab613d7-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 2, photo 14)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/6b51a99dae618b577325d5ff59d96c8205dc4503-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 15)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/092fbbc449e4a69848beaa5b1e8445372babc2c8-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 2, photo 16)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/d9234cf8dbe7d8d5bdd8de1dba3d16e7f477ec86-3600x2400.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 2, photo 17)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/e69f8d85edf633f889b0c4be8d26baa8fd9269b2-3600x2400.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 18)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/447a5cd006a125effda88ff42b5444950e76ffb5-3600x2880.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (photo 19)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/f138137a9b6f4de941f7320a4a9f1480b5ecb44c-3600x2880.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 2, photo 20)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/2d3290cc1237d4c25e5b046353e2950383663d3f-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 21)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/161a51cf0aa1b344d84a1d97ebe28cd5bd25c8ab-3600x2880.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 2, photo 22)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/363c79952b462325d1d4403ac424553cd61766ba-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 2, photo 23)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/4f1d30f71915ea334d0ff300b34d2e3226b8c1f7-3600x2400.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 24)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/5ed1a8da51270c3e0e543baf1ee43d8de62ecdba-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 25)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/1ed0240a18627a312fadad229d8fac804cc63392-3600x2400.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (photo 26)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/6a7e92d2237a4ff1314ae650e7d3489b427cf4a4-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 2, photo 27)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/f0ceb9a0f9eb663df957931b73e7200dceafd32a-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 28)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/a3828865a07b4ba1370ba9d9deac723248d7e701-3600x2400.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 29)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/b62cc22243f723d73c4cd8de8cd9629efbb7548a-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 2, photo 30)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/0b64c4b738365abf4d80d3bcaa1f96c7c2d077e3-3600x2400.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 31)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/1388f7bed1b680c8a144081b62471e5803263870-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 2, photo 32)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/86d56138014dbc1c287b56244ebec69f508c37be-2400x3600.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 33)",
  },
  {
    src: "https://cdn.sanity.io/images/1aendizt/redesign/80168db054f0c0f20db8edb3443c29a69d6bdf6d-3600x2400.jpg",
    alt: "Visionaries Summit at KidSuper Studios, Brooklyn (Day 1, photo 34)",
  },
];

export interface HighlightClip {
  /** Mux playback ID for the clip (streamed via the Mux player). Preferred
   *  when set. */
  playbackId?: string;
  /** Plain video file (mp4/mov). Fallback when no `playbackId` is set —
   *  e.g. a clip uploaded straight to Sanity's file CDN, not yet on Mux. */
  src?: string;
  /** Optional short caption (e.g. "Day one opening"). */
  label?: string;
}

/**
 * Short-form vertical clips from London. Merged into `HIGHLIGHT_CLIPS`
 * below alongside `NY_HIGHLIGHT_CLIPS`.
 */
export const LONDON_HIGHLIGHT_CLIPS: readonly HighlightClip[] = [
  // 1–6: fixed order (chosen). 7–10: the remaining clips, shuffled.
  { playbackId: "x402i7VJsq5pMiMO00GTXRjz00cGur5XHRclJQczDadnSw" },
  { playbackId: "K5tf2cD4F8REKGtZnWyUpmUDJSeIpXpwGiKgcKwwFCQ" },
  { playbackId: "zW3KzleKOl7E00OK01l3GUcuArTrr7J99zDL02zeUylYiA" },
  { playbackId: "OblvLP402JbMFHI5x01cblDcBjBghDvNA00okGPoUVoP018" },
  { playbackId: "T9QYkjH00CA9X01eBTj5nx5NLAD1ZUTkI100wVEvMBDqvM" },
  { playbackId: "yBRnzHKr2jHd7XeKmPm8TkkHxSP00NdLDecCV01Fei02bg" },
  { playbackId: "A7Y6GngTkIroNWmT7oRaVasZFzmGl1IcA7PGTbeyFFE" },
  { playbackId: "zCt1Q6752roQ02Zvt8yGGfUt01ZbI00MkohsnxEwnYSgAs" },
  { playbackId: "aDJwJx98EX6fS02u1YpQHTa2WaXolhAPHffrTWQb2Cq00" },
  { playbackId: "rHVimPetMliQWve00ZGIOOEyQj1Ers3XbEz4T2AuK90000" },
];

/**
 * New York clips — uploaded straight to Sanity's file CDN (not yet on Mux),
 * so each uses `src` rather than `playbackId` (see `HighlightClip`). Add
 * more in the same shape as `LONDON_HIGHLIGHT_CLIPS`; they appear
 * automatically in `HIGHLIGHT_CLIPS` below with no other changes needed.
 */
export const NY_HIGHLIGHT_CLIPS: readonly HighlightClip[] = [
  {
    src: "https://cdn.sanity.io/files/1aendizt/redesign/ac6601ff8441f43e315885b191330b677cf56f8f.mov",
  },
  {
    src: "https://cdn.sanity.io/files/1aendizt/redesign/bedb1d407b6868394663e4407dabb0021040ed3e.mp4",
  },
  {
    src: "https://cdn.sanity.io/files/1aendizt/redesign/a52123b49aed14a18ffb626ed769d55522df28bc.mp4",
  },
];

/**
 * Combined short-form clips row — every clip from both summits, with New
 * York's shuffled into the front alongside the first few London clips
 * (rather than just appended at the end) via `alternate`.
 */
export const HIGHLIGHT_CLIPS: readonly HighlightClip[] = alternate(
  NY_HIGHLIGHT_CLIPS,
  LONDON_HIGHLIGHT_CLIPS
);

/**
 * External homes for the full panel recordings, surfaced as a CTA under the
 * clips. Each button is hidden when its URL is empty; the whole CTA block
 * hides when both are empty.
 */
export const PANEL_LINKS = {
  // TODO: YouTube playlist URL
  youtube: "",
  // TODO: Spotify show URL
  spotify: "",
};

export interface Testimonial {
  /** Short quote from a past (London) speaker about the experience. */
  quote: string;
  /** Speaker name. */
  name: string;
  /** Speaker's brand. */
  brand: string;
  /** Optional headshot URL. */
  src?: string;
}

/**
 * Speaker testimonials from the London Summit — social proof aimed at New York
 * speaker outreach. Scaffolded with clearly-placeholder entries; these are NOT
 * real quotes.
 *
 * TODO(visionaries): real testimonials — replace each placeholder with a real
 * quote + name + brand (and optional headshot) from a London speaker.
 */
export const TESTIMONIALS: readonly Testimonial[] = [
  {
    quote:
      "In a world of AI, it is refreshing to see Wayflyer investing into IRL moments like this.",
    name: "Jojo Regan",
    brand: "Manors Golf",
    src: "https://cdn.sanity.io/images/1aendizt/redesign/1b00347b98a417899b45cac8eb307b8d3a84ac3c-960x1440.png",
  },
  {
    quote:
      "It's rare to get that level of candour and perspective in one place, and it's already sharpening how I think about the growth and strategy issues they're navigating.",
    name: "Binanti Cuzner",
    brand: "Growth Strategist",
    src: "https://cdn.sanity.io/images/1aendizt/redesign/f6d524adcc1bb1c1173d483a68f0e8baedc62b43-800x800.png",
  },
  {
    quote:
      "Some events feel like a series of sales pitches with panels that feel very surface level. This was the opposite.",
    name: "Cathal Boyle",
    brand: "Stacks Creative",
    src: "https://cdn.sanity.io/images/1aendizt/redesign/096e6bc30ae6d9ef008165f598d285d6e3598a88-800x800.jpg",
  },
  {
    quote:
      "The sessions I was in were genuinely brilliant. The content and set up were bang on, founders and senior marketing people rather than the series of pitches a lot of other summits can be.",
    name: "Kareem Raslan",
    brand: "BrainGain",
    src: "https://cdn.sanity.io/images/1aendizt/redesign/0d6fc47df4df5ca924af682a4844ca0b7a12e155-800x800.jpg",
  },
];

/** Options for the email-capture intent select. */
export const REGISTER_INTENTS = [
  "Attending",
  "Speaking",
  "Partnering",
] as const;
export type RegisterIntent = (typeof REGISTER_INTENTS)[number];

export interface VenueDetails {
  /** Venue name (used as the alt/heading fallback when no logo is set). */
  name: string;
  /** City/area label shown under the name in the `VsVenues` showcase. */
  city?: string;
  /** Optional brand/logo image shown above the heading. */
  logoSrc?: string;
  logoAlt?: string;
  /** Optional external link wrapping the primary photo. */
  websiteHref?: string;
  /** Optional Google Maps link wrapping the secondary photo. */
  mapHref?: string;
  /** Photos — when unset, a neutral placeholder tile fills the same slot. */
  primaryImage?: string;
  primaryAlt?: string;
  secondaryImage?: string;
  secondaryAlt?: string;
  mapImage?: string;
  /** Map card text. */
  mapCardTitle?: string;
  mapCardSubtitle?: string;
}

/** Default venue for the shared `VsVenue` component (Batch LDN, London). */
export const VENUE_BATCH_LDN: VenueDetails = {
  name: "Batch LDN",
  city: "Covent Garden, London",
  logoSrc:
    "https://cdn.sanity.io/images/1aendizt/redesign/f77839d2bc64e1773f91931b6568d99fb9631c6a-215x87.avif",
  logoAlt: "Batch LDN",
  websiteHref: "https://www.batchldn.com",
  mapHref: "https://maps.google.com/?q=Batch+LDN,+30+Stamford+Street,+London",
  primaryImage: ASSETS.venuePrimary,
  primaryAlt: "Batch LDN venue interior",
  secondaryImage: ASSETS.venueInterior,
  secondaryAlt: "Batch LDN second view",
  mapImage: ASSETS.venueMap,
  mapCardTitle: "Batch LDN – The Batch Member Club",
  mapCardSubtitle: "Closed · Opens 11 AM",
};

/**
 * New York venue — KidSuper Studios, 158 Roebling St, Brooklyn (Aug 26–27,
 * 2026). `secondaryImage` is a Google Maps screenshot of the area (fills the
 * full tile as the background); `mapImage` is a small OpenStreetMap crop
 * centered on the venue (© OpenStreetMap contributors, tiles from
 * tile.openstreetmap.org), composited locally with a pin marker and hosted
 * in `public/` — the small preview shown in the corner "map card" on top of
 * that background.
 */
export const VENUE_NY: VenueDetails = {
  name: "KidSuper Studios",
  city: "Brooklyn, New York",
  websiteHref: "https://kidsuper.com/",
  mapHref:
    "https://maps.google.com/?q=KidSuper+Studios,+158+Roebling+St,+Brooklyn,+NY+11211",
  primaryImage:
    "https://cdn.sanity.io/images/1aendizt/redesign/6b51a99dae618b577325d5ff59d96c8205dc4503-2400x3600.jpg",
  primaryAlt: "KidSuper Studios",
  secondaryImage:
    "https://cdn.sanity.io/images/1aendizt/redesign/0d82eb865f1493a41d072d56dd19f588cdbd9226-1238x732.png",
  secondaryAlt: "Map of KidSuper Studios, Brooklyn",
  mapImage:
    "https://cdn.sanity.io/images/1aendizt/redesign/26001251687607ccb82339fab45f78d2f6de0df4-340x220.png",
  mapCardTitle: "KidSuper Studios",
  mapCardSubtitle: "Brooklyn, New York",
};
